import {
  type BaseEditor,
  type BaseElement,
  type BaseRange,
  type BaseText,
  type Descendant,
  Editor as SlateEditor,
  type Element,
  type Node,
  Range as SlateRange,
  Transforms as SlateTransforms,
} from 'slate';
import { DOMEditor } from 'slate-dom';
import { ReactEditor } from 'slate-react';

import { isEditorEmpty } from './queries';

export const focusEditorDom = (editor: DOMEditor): void => {
  const element = DOMEditor.toDOMNode(editor, editor);
  element.focus({ preventScroll: true });
};

export const blurEditorDom = (editor: DOMEditor): void => {
  const element = DOMEditor.toDOMNode(editor, editor);
  element.blur();
};

export const focusEditor = (
  editor: BaseEditor,
  options?: { atEnd?: boolean; ignoreSelection?: boolean }
) => {
  try {
    const shouldApplySelection = options?.ignoreSelection || !editor?.selection;
    if (!isEditorEmpty(editor) && shouldApplySelection) {
      const path = options?.atEnd ? SlateEditor.end(editor, []) : SlateEditor.start(editor, []);
      SlateTransforms.select(editor, path);
    }
  } catch {
    // don't do anything
  }

  setTimeout(() => {
    try {
      focusEditorDom(editor as DOMEditor);
    } catch {
      // don't do anything
    }
  }, 50);
};

export const blurEditor = (editor: BaseEditor) => {
  try {
    blurEditorDom(editor as DOMEditor);
  } catch {
    // don't do anything
  }
};

export const editorHasValidSelection = (editor: SlateEditor): boolean => {
  const { selection } = editor;
  return (
    selection != null &&
    !SlateRange.isCollapsed(selection) &&
    SlateEditor.string(editor, selection).length > 0
  );
};

export const editorIsFocusedAndHasValidSelection = (editor: ReactEditor): boolean => {
  return ReactEditor.isFocused(editor) && editorHasValidSelection(editor);
};

/**
 * Select the whole editor content
 * @param editor
 */
export const selectWholeEditor = (editor: ReactEditor): void => {
  ReactEditor.focus(editor);
  SlateTransforms.select(editor, []);
};

/**
 * Select the current line
 * @param editor
 */
export const selectEditorLine = (editor: SlateEditor): void => {
  SlateTransforms.move(editor, {
    distance: 1,
    unit: 'line',
    reverse: true,
  });
  const startPoint = editor.selection ? editor.selection.anchor : null;

  SlateTransforms.move(editor, {
    distance: 1,
    unit: 'line',
    reverse: false,
  });
  const endPoint = editor.selection ? editor.selection.focus : null;

  if (!startPoint || !endPoint) {
    return;
  }

  SlateTransforms.select(editor, { anchor: startPoint, focus: endPoint });
};

/**
 * Select the currently focused leaf node
 * @param editor
 */
export const selectFocusedLeaf = (editor: SlateEditor): void => {
  const path = editor.selection?.anchor?.path;
  if (!path) {
    return;
  }

  const pathClone = [...path];
  pathClone.pop();

  const focusedNode = pathClone?.reduce((node: Descendant | null, pathPosition) => {
    if (!node) {
      return editor.children[pathPosition];
    }

    const element = node as Element;
    return element.children ? element.children[pathPosition] : null;
  }, null);
  const focusedElement = focusedNode as BaseElement;
  const firstLeaf = focusedElement?.children?.[0] as BaseText;

  if (!editor.selection || !firstLeaf) {
    return;
  }

  SlateTransforms.select(editor, {
    anchor: { path: editor.selection.anchor.path, offset: 0 },
    focus: { path: editor.selection.focus.path, offset: firstLeaf.text.length },
  });
};

/**
 * Select the whole editor node
 * @param editor
 * @param node
 */
export const selectWholeNode = (editor: DOMEditor, node: Node): void => {
  const path = ReactEditor.findPath(editor, node);
  try {
    if (SlateEditor.hasPath(editor, path)) {
      // Clear existing selections
      SlateTransforms.deselect(editor);

      // Select the new range
      SlateTransforms.select(editor, SlateEditor.range(editor, path));

      // Focus the editor
      ReactEditor.focus(editor);
    }
  } catch {
    // do nothing
  }
};

/**
 * Place cursor at the start of the new content
 * @param editor
 */
export const placeCursorAtStart = (editor: SlateEditor): void => {
  if (editor.isEmpty(editor)) {
    return;
  }

  const point = SlateEditor.start(editor, []);
  SlateTransforms.select(editor, point);
};

/**
 * Place cursor at the end of the new content
 * @param editor
 */
export const placeCursorAtEnd = (editor: SlateEditor): void => {
  if (editor.isEmpty(editor)) {
    return;
  }

  const point = SlateEditor.end(editor, []);
  SlateTransforms.select(editor, point);
};

/**
 * Convert Slate range to DOM range
 * @param editor
 * @param slateRange
 */
export const convertSlateRangeToDomRange = (
  editor: DOMEditor,
  slateRange?: BaseRange
): Range | undefined => {
  try {
    if (!slateRange) {
      return undefined;
    }

    if (!ReactEditor.hasRange(editor, slateRange)) {
      return undefined;
    }

    return ReactEditor.toDOMRange(editor, slateRange);
  } catch {
    return undefined;
  }
};
